import axios, { AxiosInstance } from 'axios';
import { getVoiceFallbackClient } from './_shared';

// ── Transfer pools & destinations (/api/pools, /api/destinations) ─────
// Verified 2026-08-19 against the live PoolResponse/DestinationResponse/
// AddDestinationRequest/UpdateDestinationRequest/UpdatePoolRequest schema on
// /openapi.json. Distinct from — and additional to — the campaign's own flat
// transfer_numbers/transfer_directory (WarmTransferDestinationsField.tsx):
// this is the newer priority/weight/strategy/presence-aware pool system.
// There is no bare POST to create a pool (pools are auto-provisioned per
// campaign) and no DELETE for a destination — only PATCH exists for either.

export type TransferPoolStrategy = string; // backend declares this as an unconstrained string, not an enum
export type DestinationPresenceStatus = 'available' | 'unavailable';

export type TransferDestinationRecord = {
  id: string;
  name: string | null;
  department: string | null;
  e164: string;
  hours: string | Record<string, unknown> | null;
  priority: number;
  weight: number | null;
  availability: DestinationAvailability;
  manual_status: string | null;
  manual_status_expires_at: string | null;
  last_used_at: string | null;
};

export type TransferPool = {
  id: string;
  /** null on the workspace default pool, which sets `workspace_id` instead. */
  campaign_id: string | null;
  workspace_id?: string | null;
  strategy: TransferPoolStrategy;
  ring_timeout_sec: number;
  simultaneous_cap: number;
  on_exhausted: string;
  rr_pointer: string | null;
  destinations: TransferDestinationRecord[];
};

/** `GET /api/campaigns/{campaign_id}/transfer-pool` — the backend declares no
 * response schema for this route; treated defensively as possibly null (a
 * campaign may not have a pool provisioned yet). */
export async function fetchCampaignTransferPool(campaignId: string, axiosInstance?: AxiosInstance): Promise<TransferPool | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<TransferPool | null>(`/api/campaigns/${campaignId}/transfer-pool`);
  return response.data ?? null;
}

/** `GET /api/workspaces/current/transfer-pool-default` — confirmed live 2026-10-23. Same shape as a campaign pool
 * (`campaign_id` null, `workspace_id` set), created lazily on first call. It's a live shared fallback: when a call
 * needs a human and the campaign's own pool has nobody available, the backend checks this pool at that moment. There's
 * no PATCH for it — edit it with `updateTransferPool` / the destination functions, passing this pool's `id`. */
export async function fetchWorkspaceDefaultTransferPool(axiosInstance?: AxiosInstance): Promise<TransferPool> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<TransferPool>('/api/workspaces/current/transfer-pool-default');
  return response.data;
}

/** `PATCH /api/pools/{pool_id}` — update pool-level routing settings. */
export async function updateTransferPool(
  poolId: string,
  patch: { strategy?: string; ring_timeout_sec?: number; simultaneous_cap?: number; on_exhausted?: string },
  axiosInstance?: AxiosInstance
): Promise<TransferPool> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.patch<TransferPool>(`/api/pools/${poolId}`, patch);
  return response.data;
}

/** `DestinationResponse`/`AddDestinationRequest`/`UpdateDestinationRequest`.availability
 * — confirmed live 2026-08-21 against the real consumer,
 * `services/destination_availability_service.py` (read at call-transfer time).
 * `source` must be exactly one of these three strings; anything else silently falls back to
 * 'hours' behavior. The event type id is nested under `calendar`, not a
 * top-level `calcom_event_type_id` (that was an earlier, wrong guess). 'oncall'
 * is a real enum value but isn't wired to a provider yet — it always reports
 * unavailable — so the UI below doesn't offer it. */
export type DestinationAvailability =
  | { source: 'hours' }
  | { source: 'oncall' }
  | { source: 'calendar'; calendar?: { provider: 'cal.com'; event_type_id: string } };

/** `POST /api/pools/{pool_id}/destinations` — add a destination to a pool. */
export async function addTransferDestination(
  poolId: string,
  payload: { name?: string; department?: string; e164: string; hours?: string; priority?: number; weight?: number; availability?: DestinationAvailability },
  axiosInstance?: AxiosInstance
): Promise<TransferDestinationRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<TransferDestinationRecord>(`/api/pools/${poolId}/destinations`, payload);
  return response.data;
}

/** `PATCH /api/destinations/{destination_id}` — update a destination. */
export async function updateTransferDestination(
  destinationId: string,
  patch: { name?: string; department?: string; e164?: string; hours?: string; priority?: number; weight?: number; availability?: DestinationAvailability },
  axiosInstance?: AxiosInstance
): Promise<TransferDestinationRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.patch<TransferDestinationRecord>(`/api/destinations/${destinationId}`, patch);
  return response.data;
}

/** `POST /api/destinations/{destination_id}/presence` — manual available/
 * unavailable toggle (Slack/mobile use case); auto-reverts after ttl_hours. */
export async function setDestinationPresence(
  destinationId: string,
  status: DestinationPresenceStatus,
  ttlHours?: number,
  axiosInstance?: AxiosInstance
): Promise<TransferDestinationRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<TransferDestinationRecord>(`/api/destinations/${destinationId}/presence`, {
    status,
    ...(ttlHours != null ? { ttl_hours: ttlHours } : {}),
  });
  return response.data;
}
